import { UsageBuffer } from "./usage-buffer";

/**
 * Counts presses of the UI's controls.
 *
 * One passive click listener for the whole app: it does no work until something
 * is actually pressed, and never touches the move/scroll path. A press is
 * attributed to the nearest ancestor that names itself, so nothing needs to opt
 * in per call site.
 *
 * Names come from `data-analytics` (preferred) or `aria-label`, never from an
 * element's text content — text would pull live readings and captain names into
 * the payload and let the key space grow without bound.
 */

/** How far up the tree to look for a control that names itself */
const LABEL_ANCESTOR_LIMIT = 6;

const NAME_MAX_LENGTH = 40;

const INTERACTIVE_TAGS = [
  "BUTTON",
  "A",
  "INPUT",
  "SELECT",
  "TEXTAREA",
  "LABEL",
];

/** Normalises a label into a stable, low-cardinality key */
const toKey = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9._:-]/g, "")
    .slice(0, NAME_MAX_LENGTH);

const isInteractive = (element: HTMLElement): boolean =>
  INTERACTIVE_TAGS.indexOf(element.tagName) !== -1 ||
  element.getAttribute("role") === "button" ||
  typeof element.onclick === "function";

/**
 * Resolves the pressed control to a name, or null when the press did not land on
 * anything identifiable (e.g. a gauge or the page background).
 */
const resolvePressName = (target: EventTarget | null): string | null => {
  let node = target as HTMLElement | null;
  let fallback: string | null = null;

  for (let i = 0; i < LABEL_ANCESTOR_LIMIT && node; i++) {
    if (!node.getAttribute) return fallback;

    const explicit = node.getAttribute("data-analytics");
    if (explicit) return toKey(explicit);

    const ariaLabel = node.getAttribute("aria-label");
    if (ariaLabel) return toKey(ariaLabel);

    // Remember that we passed a control, so an unlabelled button still counts
    if (!fallback && isInteractive(node)) {
      fallback = node.tagName.toLowerCase();
    }

    node = node.parentElement;
  }

  return fallback;
};

export const startPressTracking = (buffer: UsageBuffer): (() => void) => {
  const onClick = (event: Event) => {
    const name = resolvePressName(event.target);
    if (name) buffer.recordPress(name);
  };

  // Capture phase so a handler calling stopPropagation cannot hide the press
  window.addEventListener("click", onClick, true);

  return () => {
    window.removeEventListener("click", onClick, true);
  };
};
